import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery, toast } from '@grandvisionhq/common';
import { usePlacePredictions, mapPlacePredictionToSuggestion } from '@grandvisionhq/locations';
import { useIntl, useFeatureToggles, useClickAndReserveFeatureConfig } from '@grandvisionhq/state';
import { useTealiumEventProperties, useDispatchTrackEvent } from '@grandvisionhq/svc-tealium/events';
import { useSessionStorage, useDebouncedEffect } from '@grandvisionhq/utils';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import { replaceStoreFinderUrlWithNewQueryParams } from '../../utils/store-utils.js';
import { getAddressFromSuggestions } from '../../utils/get-address-from-suggestions.js';
import 'lodash/deburr';
import '../../context/StoreFinderSlots/context.js';
import { storeSearchSubmitEvent } from '../../data-layer/actions.js';
import usePlaceById from '../../hooks/api/use-place-by-id.js';
import '@grandvisionhq/graphql';
import '../../operations/queries/get-place-by-id.js';
import '../../operations/queries/get-stores-by-distance.js';
import '../../operations/queries/get-store-by-code.js';
import '../../operations/queries/get-store-details.js';
import '../../operations/queries/get-nearest-stores.js';
import '../../operations/queries/get-preferred-store.js';
import '../../operations/queries/get-preferred-store-code.js';
import '../../operations/queries/stores-search.js';
import '../../types.js';
import useGetStoresByDistanceHook from '../../hooks/api/use-get-stores-by-distance-hook.js';
import '../../operations/mutations/update-personal-details.js';
import useStoreFinderUrlParams from './hooks/use-store-finder-url-params.js';
import StoreSearchForm from './StoreSearchForm.js';

const scrollToFormHandlerId = 'location-search-form';
const MIN_SEARCH_CHARACTER_COUNT = 2;
const StoreSearchFormContainer = (_param)=>{
    var { appointmentTypeId , minSearchCharacterCount =MIN_SEARCH_CHARACTER_COUNT , storeQueryFilter , storeQueryForceDistance =true , queryResultsFilters  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "appointmentTypeId",
        "minSearchCharacterCount",
        "storeQueryFilter",
        "storeQueryForceDistance",
        "queryResultsFilters"
    ]);
    var ref1;
    const { getLabel  } = useIntl();
    const { config , searchQuery , isPristineSearch , suggestions , isLocationSelected , userLocation , isUsingUserLocation , isUserLocationLoading , useUserLocationError , setIsUserLocationRequested , clearSearch , setSelectedLocation , setSuggestions , setIsPristineSearch , setStores , setSearchQuery , setIsStoresLoading , sku , events , setActiveMapInfoWindow ,  } = useStoreFinderContext();
    const searchRef = /*#__PURE__*/ React.createRef();
    const { featureConfigs  } = useFeatureToggles();
    const maxSearchStoresResults = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.maxSearchStoresResults) !== undefined ? featureConfigs.maxSearchStoresResults : 30;
    const { withUrlParams , storeFilter  } = config;
    const isMobile = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.PHONE;
    const clickAndReserveMode = useClickAndReserveFeatureConfig();
    const [isWaitingForSuggestions, setIsWaitingForSuggestions] = React.useState(false);
    const [getPlacePredictions, placePredictions] = usePlacePredictions({
        variables: {
            name: searchQuery
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
        onError: (error)=>{
            toast.failed('stores.page.search.getPlacePredictions.error');
            console.error('Failed to fetch place predictions: ', error);
        }
    });
    const [getPlaceById, placeById] = usePlaceById({
        id: ''
    });
    const useStoresByDistance = useGetStoresByDistanceHook(sku);
    const [getStoresByDistance, storesByDistance] = useStoresByDistance({
        searchGeoLocation: {
            lat: '',
            lon: ''
        },
        filter: storeQueryFilter,
        sku: sku !== null && sku !== void 0 ? sku : '',
        forceDistance: storeQueryForceDistance
    });
    const [userLocationInSessionStorage, setUserLocationInSessionStorage] = useSessionStorage('grandvision_user_location', undefined, (value)=>value === undefined
    );
    const labels = React.useMemo(()=>({
            placeholder: getLabel('stores.page.search.searchForm.placeholder'),
            noResults: getLabel('stores.page.autocomplete.noResults'),
            useMyCurrentLocation: getLabel('stores.page.search.useMyCurrentLocation'),
            myCurrentLocationPlaceholder: getLabel('stores.page.search.useMyCurrentLocation.placeholder'),
            noAvailableResultsScreenReaderHint: getLabel('elements.autocomplete.noAvailableResultsScreenReaderHint')
        })
    , [
        getLabel
    ]);
    const onStoreSearchSubmitEvent = React.useCallback(({ location , interaction  })=>{
        var ref;
        const dataLayerEvent = storeSearchSubmitEvent({
            interaction,
            location,
            searchResults: ((ref = storesByDistance.data) === null || ref === void 0 ? void 0 : ref.length) || null
        });
        DataLayer.pushEvent(dataLayerEvent);
    }, [
        storesByDistance.data
    ]);
    const { commonEventProperties , getCommonUserProperties  } = useTealiumEventProperties();
    const storeSearchResultsEvent = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        var ref;
        const commonUserProperties = yield getCommonUserProperties();
        if (!commonUserProperties) return;
        var ref2;
        return swcHelpers.objectSpread({
            id: 'Event',
            Events_FunnelStoreSearch: '1',
            Store_Search_Keyword: searchQuery,
            Store_Search_ResultItemsQnt: (ref2 = (ref = storesByDistance.data) === null || ref === void 0 ? void 0 : ref.length.toString()) !== null && ref2 !== void 0 ? ref2 : '0',
            Store_Search_Type: isUsingUserLocation ? 'geolocalized' : 'text'
        }, commonEventProperties, commonUserProperties);
    }), [
        commonEventProperties,
        getCommonUserProperties,
        isUsingUserLocation,
        searchQuery,
        (ref1 = storesByDistance.data) === null || ref1 === void 0 ? void 0 : ref1.length, 
    ]);
    const handleOnChange = React.useCallback((value)=>{
        setSearchQuery(value);
        setIsWaitingForSuggestions(false);
        // In case selected location and value in the search field are the same,
        // nothing changed in the input and we can keep selected location
        setSelectedLocation((prevLocation)=>prevLocation === value ? prevLocation : ''
        );
    }, [
        setSearchQuery,
        setSelectedLocation
    ]);
    /** Hook to build store finder URL based on search state,
   *  to handle initial page state on first page load based on query parameters in URL
   */ const newQueryParams = React.useMemo(()=>isUsingUserLocation ? {
            userLocation: 'true'
        } : {
            search: searchQuery
        }
    , [
        isUsingUserLocation,
        searchQuery
    ]);
    const initialSearchQuery = useStoreFinderUrlParams({
        handleOnChange,
        setIsUserLocation: setIsUserLocationRequested,
        newQueryParams,
        enabled: withUrlParams
    });
    const getStoresByLocation = React.useCallback((id)=>{
        getPlaceById({
            variables: {
                id
            }
        });
        setIsStoresLoading(true);
        const address = getAddressFromSuggestions(suggestions, id);
        setSelectedLocation(address);
        withUrlParams && replaceStoreFinderUrlWithNewQueryParams({
            search: address
        });
        setSuggestions([]);
        if (isPristineSearch) {
            setIsPristineSearch(false);
        }
    }, [
        getPlaceById,
        setSelectedLocation,
        setSuggestions,
        setIsStoresLoading,
        isPristineSearch,
        suggestions,
        setIsPristineSearch,
        withUrlParams, 
    ]);
    const onSuggestionClick = React.useCallback((id)=>{
        getStoresByLocation(id);
    }, [
        getStoresByLocation
    ]);
    const onChange = React.useCallback((event)=>handleOnChange(event.currentTarget.value)
    , [
        handleOnChange
    ]);
    const onSubmit = React.useCallback(()=>{
        setActiveMapInfoWindow(null);
        if (suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
        } else {
            setIsWaitingForSuggestions(true);
        }
    }, [
        setActiveMapInfoWindow,
        getStoresByLocation,
        setIsWaitingForSuggestions,
        suggestions
    ]);
    React.useEffect(()=>{
        if (isUsingUserLocation) {
            setSearchQuery(labels.myCurrentLocationPlaceholder);
            setSelectedLocation(labels.myCurrentLocationPlaceholder);
        }
    }, [
        isUsingUserLocation,
        labels.myCurrentLocationPlaceholder,
        setSearchQuery,
        setSelectedLocation, 
    ]);
    const handleOnSearch = React.useCallback(()=>{
        if (searchQuery.length >= minSearchCharacterCount && !isLocationSelected && !isUsingUserLocation) {
            getPlacePredictions({
                variables: {
                    name: searchQuery
                }
            });
        }
    }, [
        searchQuery,
        minSearchCharacterCount,
        isLocationSelected,
        isUsingUserLocation,
        getPlacePredictions, 
    ]);
    const onUseMyCurrentLocation = React.useCallback((clickLocation)=>{
        setIsUserLocationRequested(true);
        setIsWaitingForSuggestions(false);
        if (clickAndReserveMode) {
            var ref;
            events === null || events === void 0 ? void 0 : (ref = events.onClickLocationEvent) === null || ref === void 0 ? void 0 : ref.call(events, clickAndReserveMode, clickLocation);
        }
    }, [
        setIsUserLocationRequested,
        setIsWaitingForSuggestions,
        events,
        clickAndReserveMode
    ]);
    useDebouncedEffect(()=>{
        handleOnSearch();
    }, 500, [
        searchQuery
    ]);
    React.useEffect(()=>{
        if (!placePredictions.data) {
            return;
        }
        setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
    }, [
        placePredictions.data
    ]);
    React.useEffect(()=>{
        if (storesByDistance.error || placeById.error) {
            setIsStoresLoading(false);
        }
    }, [
        storesByDistance.error,
        placeById.error,
        setIsStoresLoading
    ]);
    React.useEffect(()=>{
        if (useUserLocationError) {
            setSearchQuery('');
            setSelectedLocation('');
            setIsUserLocationRequested(false);
            onStoreSearchSubmitEvent({
                location: null,
                interaction: 'current location'
            });
        }
    }, [
        useUserLocationError,
        setSearchQuery,
        setSelectedLocation,
        setIsUserLocationRequested
    ]);
    React.useEffect(()=>{
        if (!placeById.data) {
            return;
        }
        const { coordinates: { lat , lon  } ,  } = placeById.data;
        const deviceGeoLocation = userLocation ? {
            lat: userLocation.lat,
            lon: userLocation.lng
        } : userLocationInSessionStorage && Object.keys(userLocationInSessionStorage).length !== 0 ? {
            lat: userLocationInSessionStorage.lat,
            lon: userLocationInSessionStorage.lng
        } : undefined;
        getStoresByDistance({
            variables: {
                query: appointmentTypeId,
                maxResults: maxSearchStoresResults,
                searchGeoLocation: {
                    lat,
                    lon
                },
                deviceGeoLocation,
                filter: storeQueryFilter,
                forceDistance: storeQueryForceDistance,
                sku: sku !== null && sku !== void 0 ? sku : ''
            }
        });
    // Effect should only run on changing placeById.data or appointmentTypeId
    }, [
        placeById.data,
        appointmentTypeId,
        storeQueryForceDistance,
        sku
    ]);
    const dispatchTrackEvent = useDispatchTrackEvent();
    React.useEffect(()=>{
        var ref;
        if (!storesByDistance.data) {
            return;
        }
        onStoreSearchSubmitEvent({
            location: storesByDistance.data.length > 0 && storesByDistance.data[0].town,
            interaction: isUsingUserLocation && userLocation !== undefined ? 'current location' : 'location typed'
        });
        dispatchTrackEvent(storeSearchResultsEvent);
        setStores(storesByDistance.data.filter(storeFilter));
        setIsStoresLoading(false);
        (ref = searchRef.current) === null || ref === void 0 ? void 0 : ref.blur();
        if (isMobile && storesByDistance.data.length > 0) {
            var // Can't use ref in this case because we have two search forms on the page.
            // During the first search ref in useEffect points to the old search field
            // So we need to get needed search form by id.
            // Alternative way ref.current?.scrollIntoView(), but it doesn't work for the first search.
            ref3;
            (ref3 = document.getElementById(scrollToFormHandlerId)) === null || ref3 === void 0 ? void 0 : ref3.scrollIntoView();
        }
    }, [
        storesByDistance.data
    ]);
    React.useEffect(()=>{
        if (searchQuery.length < minSearchCharacterCount) {
            setSuggestions([]);
        }
    }, [
        searchQuery,
        minSearchCharacterCount,
        setSuggestions
    ]);
    React.useEffect(()=>{
        if (!userLocation) {
            return;
        }
        if (isPristineSearch) {
            setIsPristineSearch(false);
        }
        setUserLocationInSessionStorage(userLocation);
        const { lat , lng: lon  } = userLocation;
        getStoresByDistance({
            variables: {
                query: appointmentTypeId,
                maxResults: maxSearchStoresResults,
                searchGeoLocation: {
                    lat,
                    lon
                },
                deviceGeoLocation: {
                    lat,
                    lon
                },
                forceDistance: storeQueryForceDistance,
                sku: sku !== null && sku !== void 0 ? sku : ''
            }
        });
        setIsStoresLoading(true);
    }, [
        userLocation,
        appointmentTypeId,
        isPristineSearch,
        storeQueryForceDistance,
        sku
    ]);
    /** Should select first suggestion if already waiting for suggestions list */ React.useEffect(()=>{
        if (isWaitingForSuggestions && suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
            setIsWaitingForSuggestions(false);
        }
    }, [
        suggestions,
        isWaitingForSuggestions,
        getStoresByLocation,
        setIsWaitingForSuggestions
    ]);
    React.useEffect(()=>{
        if (initialSearchQuery === searchQuery && isPristineSearch && suggestions.length > 0) {
            getStoresByLocation(suggestions[0].id);
        }
    }, [
        initialSearchQuery,
        searchQuery,
        isPristineSearch,
        suggestions,
        getStoresByLocation
    ]);
    // Dispatch keyup event on Enter and Space manually after pressing key on Use my location from the dropdown.
    // After pressing Use my location from the dropdown input becomes disabled,
    // when input disabled key events don't trigger on it.
    // Need to reset the keypress state to handle cursor correctly.
    React.useEffect(()=>{
        var ref, ref4;
        if (!isUsingUserLocation) {
            return;
        }
        (ref = searchRef.current) === null || ref === void 0 ? void 0 : ref.dispatchEvent(new KeyboardEvent('keyup', {
            code: 'Enter'
        }));
        (ref4 = searchRef.current) === null || ref4 === void 0 ? void 0 : ref4.dispatchEvent(new KeyboardEvent('keyup', {
            code: 'Space'
        }));
    }, [
        isUsingUserLocation,
        searchRef
    ]);
    return /*#__PURE__*/ React.createElement(StoreSearchForm, swcHelpers.extends({}, rest, {
        searchRef: searchRef,
        labels: labels,
        loading: placePredictions.loading || isUserLocationLoading,
        showClearButton: searchQuery.length > 0,
        searchQuery: searchQuery,
        suggestions: suggestions,
        onChange: onChange,
        onSubmit: onSubmit,
        onClear: clearSearch,
        onSuggestionClick: onSuggestionClick,
        onSuggestionTopContentClick: ()=>onUseMyCurrentLocation('search suggestion')
        ,
        onUseMyCurrentLocationClick: ()=>onUseMyCurrentLocation('button')
        ,
        isSuggestionSelected: isLocationSelected,
        isFocusedOnMount: true,
        isUsingUserLocation: isUsingUserLocation,
        minSearchCharacterCount: minSearchCharacterCount,
        queryResultsFilters: queryResultsFilters
    }));
};

export { StoreSearchFormContainer as default };
//# sourceMappingURL=StoreSearchFormContainer.js.map
